import React, { useEffect, useMemo, useState } from "react";
import { api, API, money } from "@/lib/api";
import Money from "@/components/Money";
import { toast } from "sonner";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import {
  BarChart3, Download, Filter, TrendingUp, CheckCircle2,
  AlertTriangle, Users, Wallet, Package, MapPinned, PieChart as PieIcon,
  Loader2, FileSpreadsheet,
} from "lucide-react";

const METODO_LABEL = { dinheiro: "Dinheiro", pix: "Pix", credito: "Cartão Crédito", debito: "Cartão Débito" };
const PAG_COLORS = { dinheiro: "#15803d", pix: "#0ea5e9", credito: "#C85A32", debito: "#f59e0b" };

function todayIso() { return new Date().toISOString().slice(0, 10); }
function monthStartIso() { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); }

export default function Relatorios() {
  const [filters, setFilters] = useState({
    inicio: monthStartIso(),
    fim: todayIso(),
    rota_id: "",
    metodo: "",
    status: "all",
    granularidade: "dia",
  });
  const [rotas, setRotas] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => { api.get("/rotas").then((r) => setRotas(r.data)).catch(() => {}); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      for (const k of Object.keys(filters)) {
        if (filters[k] !== "" && filters[k] != null) params[k] = filters[k];
      }
      const r = await api.get("/relatorios", { params });
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar relatório");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [filters]);

  const exportar = async () => {
    setExporting(true);
    try {
      const params = {};
      for (const k of Object.keys(filters)) {
        if (filters[k] !== "" && filters[k] != null) params[k] = filters[k];
      }
      const token = localStorage.getItem("cf_token") || "";
      const q = new URLSearchParams(params).toString();
      const res = await fetch(`${API}/api/relatorios/export.xlsx?${q}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Falha ao exportar");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `relatorio-carnefacil-${todayIso()}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Relatório exportado");
    } catch (err) {
      toast.error(err?.message || "Erro ao exportar");
    } finally {
      setExporting(false);
    }
  };

  const formasData = useMemo(() =>
    (data?.formas_pagamento || []).map((f) => ({
      name: METODO_LABEL[f.metodo] || f.metodo, value: f.valor, metodo: f.metodo,
    })), [data]);

  const serieData = useMemo(() => data?.vendas_periodo || [], [data]);

  return (
    <div>
      <header className="mb-5">
        <div className="text-xs font-bold uppercase tracking-wider text-[color:var(--terracotta)] inline-flex items-center gap-1">
          <BarChart3 className="w-3.5 h-3.5" /> Painel Analítico
        </div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-stone-900">Relatórios</h1>
        <p className="text-sm text-stone-500 mt-1">Métricas, gráficos e export dos dados reais do seu negócio.</p>
      </header>

      {/* Filtros */}
      <div className="carne-card p-4 mb-4" data-testid="rel-filtros">
        <div className="text-[11px] font-bold uppercase tracking-wider text-stone-500 flex items-center gap-1 mb-2">
          <Filter className="w-3 h-3" /> Filtros
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-stone-500">Início</label>
            <input data-testid="rel-f-inicio" type="date" value={filters.inicio} onChange={(e) => setFilters({ ...filters, inicio: e.target.value })} className="mt-1 w-full h-10 px-2 rounded-lg border border-stone-200 bg-white text-sm" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-stone-500">Fim</label>
            <input data-testid="rel-f-fim" type="date" value={filters.fim} onChange={(e) => setFilters({ ...filters, fim: e.target.value })} className="mt-1 w-full h-10 px-2 rounded-lg border border-stone-200 bg-white text-sm" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-stone-500">Rota</label>
            <select data-testid="rel-f-rota" value={filters.rota_id} onChange={(e) => setFilters({ ...filters, rota_id: e.target.value })} className="mt-1 w-full h-10 px-2 rounded-lg border border-stone-200 bg-white text-sm">
              <option value="">Todas</option>
              {rotas.map((r) => <option key={r.id} value={r.id}>{r.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-stone-500">Forma de pag.</label>
            <select data-testid="rel-f-metodo" value={filters.metodo} onChange={(e) => setFilters({ ...filters, metodo: e.target.value })} className="mt-1 w-full h-10 px-2 rounded-lg border border-stone-200 bg-white text-sm">
              <option value="">Todas</option>
              <option value="dinheiro">💵 Dinheiro</option>
              <option value="pix">⚡ Pix</option>
              <option value="credito">💳 Crédito</option>
              <option value="debito">💳 Débito</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-stone-500">Status</label>
            <select data-testid="rel-f-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="mt-1 w-full h-10 px-2 rounded-lg border border-stone-200 bg-white text-sm">
              <option value="all">Todos</option>
              <option value="pago">Pagos</option>
              <option value="pendente">Pendentes</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-stone-500">Granularidade</label>
            <select data-testid="rel-f-gran" value={filters.granularidade} onChange={(e) => setFilters({ ...filters, granularidade: e.target.value })} className="mt-1 w-full h-10 px-2 rounded-lg border border-stone-200 bg-white text-sm">
              <option value="dia">Dia</option>
              <option value="semana">Semana</option>
              <option value="mes">Mês</option>
            </select>
          </div>
        </div>
        <button
          data-testid="rel-btn-export"
          onClick={exportar}
          disabled={exporting || !data}
          className="btn-primary mt-3 w-full"
        >
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Exportar Excel
        </button>
      </div>

      {loading && <div className="text-stone-500 text-sm">Carregando…</div>}

      {!loading && data && (
        <>
          {/* Métricas principais */}
          <section className="mb-6" data-testid="rel-metricas">
            <h2 className="font-display text-base font-bold text-stone-900 mb-2 inline-flex items-center gap-1">
              <TrendingUp className="w-4 h-4" /> Principais métricas
            </h2>
            <div className="grid grid-cols-2 gap-2">
              <Card label="Valor vendido" value={<Money v={data.metricas.valor_total_vendido} />} tone="warn" />
              <Card label="Recebido" value={<Money v={data.metricas.total_recebido} />} tone="ok" />
              <Card label="Pendente" value={<Money v={data.metricas.total_pendente} />} tone={data.metricas.total_pendente > 0 ? "danger" : "default"} />
              <Card label="% Recebido" value={`${data.metricas.percentual_recebido.toFixed(1)}%`} tone="ok" />
              <Card label="Vendas" value={data.metricas.qtd_vendas} />
              <Card label="Clientes ativos" value={data.metricas.qtd_clientes} />
              <Card label="Ticket médio" value={<Money v={data.metricas.ticket_medio} />} />
              <Card label="Inadimplência" value={`${data.cobrancas.percentual_inadimplencia.toFixed(1)}%`} tone={data.cobrancas.percentual_inadimplencia > 20 ? "danger" : "default"} />
            </div>
          </section>

          {/* Formas de pagamento */}
          <section className="carne-card p-4 mb-4" data-testid="rel-formas">
            <h2 className="font-display text-base font-bold text-stone-900 mb-2 inline-flex items-center gap-1">
              <PieIcon className="w-4 h-4" /> Formas de pagamento
            </h2>
            {formasData.length === 0 ? (
              <p className="text-sm text-stone-500 py-6 text-center">Nenhum pagamento com forma registrada nesse período.</p>
            ) : (
              <>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={formasData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} label={(e) => `${e.name}`}>
                        {formasData.map((d, i) => (<Cell key={i} fill={PAG_COLORS[d.metodo] || "#C85A32"} />))}
                      </Pie>
                      <Tooltip formatter={(v) => money(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {data.formas_pagamento.map((f) => (
                    <div key={f.metodo} className="flex items-center justify-between rounded-lg bg-stone-50 px-3 py-2" data-testid={`rel-forma-${f.metodo}`}>
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: PAG_COLORS[f.metodo] }}></span>
                        <span className="text-xs font-bold text-stone-700">{METODO_LABEL[f.metodo]}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-mono-num text-sm font-bold text-stone-900"><Money v={f.valor} /></div>
                        <div className="text-[10px] text-stone-500">{f.qtd} baixas</div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>

          {/* Vendas por período */}
          <section className="carne-card p-4 mb-4" data-testid="rel-serie">
            <h2 className="font-display text-base font-bold text-stone-900 mb-2 inline-flex items-center gap-1">
              <BarChart3 className="w-4 h-4" /> Evolução — {filters.granularidade === "dia" ? "por dia" : filters.granularidade === "semana" ? "por semana" : "por mês"}
            </h2>
            {serieData.length === 0 ? (
              <p className="text-sm text-stone-500 py-6 text-center">Sem vendas no período.</p>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={serieData}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => money(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="valor" stroke="#C85A32" strokeWidth={2} name="Vendido" dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="recebido" stroke="#15803d" strokeWidth={2} name="Recebido" dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </section>

          {/* Cobranças */}
          <section className="carne-card p-4 mb-4" data-testid="rel-cobrancas">
            <h2 className="font-display text-base font-bold text-stone-900 mb-2 inline-flex items-center gap-1">
              <Wallet className="w-4 h-4" /> Cobranças
            </h2>
            <div className="grid grid-cols-3 gap-2">
              <MiniStat label="Parcelas" value={`${data.cobrancas.parcelas_pagas}/${data.cobrancas.parcelas_total}`} sub="pagas" />
              <MiniStat label="Pendentes" value={data.cobrancas.parcelas_pendentes} sub="parcelas" tone="warn" />
              <MiniStat label="Inadimpl." value={`${data.cobrancas.percentual_inadimplencia.toFixed(1)}%`} tone={data.cobrancas.percentual_inadimplencia > 20 ? "danger" : "default"} />
            </div>
            <div className="h-2 bg-stone-200 rounded-full mt-3 overflow-hidden">
              <div
                className="h-full bg-emerald-500 transition-all"
                style={{ width: `${data.cobrancas.parcelas_total ? (data.cobrancas.parcelas_pagas / data.cobrancas.parcelas_total) * 100 : 0}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-[11px] text-stone-500 mt-1">
              <span>Recebido <Money v={data.cobrancas.valor_recebido} /></span>
              <span>Pendente <Money v={data.cobrancas.valor_pendente} /></span>
            </div>
          </section>

          {/* Rotas */}
          <section className="carne-card p-4 mb-4" data-testid="rel-rotas">
            <h2 className="font-display text-base font-bold text-stone-900 mb-2 inline-flex items-center gap-1">
              <MapPinned className="w-4 h-4" /> Rotas
            </h2>
            {data.rotas.length === 0 ? (
              <p className="text-sm text-stone-500 py-6 text-center">Nenhuma rota com movimento no período.</p>
            ) : (
              <>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.rotas.slice(0, 8)}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis dataKey="nome" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(v) => money(v)} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="recebido" fill="#15803d" name="Recebido" />
                      <Bar dataKey="pendente" fill="#dc2626" name="Pendente" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="divide-y divide-stone-100 mt-3">
                  {data.rotas.map((r) => (
                    <div key={r.id || "sem"} className="flex items-center justify-between py-2 gap-2" data-testid={`rel-rota-${r.id || "sem"}`}>
                      <div className="min-w-0">
                        <div className="font-bold text-sm text-stone-900 truncate">{r.nome}</div>
                        <div className="text-[11px] text-stone-500">{r.clientes} cliente{r.clientes !== 1 ? "s" : ""} · {r.vendas} venda{r.vendas !== 1 ? "s" : ""}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[11px] text-emerald-700 font-mono-num">+<Money v={r.recebido} /></div>
                        <div className="text-[11px] text-rose-700 font-mono-num">-<Money v={r.pendente} /></div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>

          {/* Clientes */}
          <section className="carne-card p-4 mb-4" data-testid="rel-clientes">
            <h2 className="font-display text-base font-bold text-stone-900 mb-2 inline-flex items-center gap-1">
              <Users className="w-4 h-4" /> Clientes
            </h2>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <MiniStat label="Cadastrados" value={data.clientes.total_cadastrados} />
              <MiniStat label="Com pendência" value={data.clientes.com_pendencia} tone="danger" />
              <MiniStat label="Em dia" value={data.clientes.em_dia} tone="ok" />
            </div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-stone-500 mb-1">Top 10 por valor comprado</div>
            {data.clientes.top_10.length === 0 ? (
              <p className="text-sm text-stone-500 py-4 text-center">Sem dados no período.</p>
            ) : (
              <div className="divide-y divide-stone-100">
                {data.clientes.top_10.map((c, i) => (
                  <div key={c.id} className="flex items-center justify-between py-2 gap-2" data-testid={`rel-top-cli-${c.id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-7 h-7 rounded-full bg-[color:var(--terracotta-light)] text-[color:var(--terracotta)] font-bold text-xs flex items-center justify-center shrink-0">
                        {i + 1}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-bold text-stone-900 truncate">{c.nome}</div>
                        {c.rota_nome && <div className="text-[10px] text-stone-500">{c.rota_nome}</div>}
                      </div>
                    </div>
                    <div className="font-mono-num text-sm font-bold text-stone-900 shrink-0"><Money v={c.valor_comprado} /></div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Produtos */}
          <section className="carne-card p-4 mb-6" data-testid="rel-produtos">
            <h2 className="font-display text-base font-bold text-stone-900 mb-2 inline-flex items-center gap-1">
              <Package className="w-4 h-4" /> Produtos mais vendidos
            </h2>
            <div className="text-[11px] text-stone-500 mb-2">Baseado na descrição das vendas registradas.</div>
            {data.produtos.length === 0 ? (
              <p className="text-sm text-stone-500 py-4 text-center">Sem produtos no período.</p>
            ) : (
              <div className="divide-y divide-stone-100">
                {data.produtos.map((p, i) => (
                  <div key={i} className="flex items-center justify-between py-2 gap-2" data-testid={`rel-produto-${i}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-bold text-stone-900 truncate">{p.descricao}</div>
                      <div className="text-[10px] text-stone-500">{p.qtd} venda{p.qtd !== 1 ? "s" : ""}</div>
                    </div>
                    <div className="font-mono-num text-sm font-bold text-stone-900 shrink-0"><Money v={p.valor} /></div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function Card({ label, value, tone = "default" }) {
  const tones = {
    default: "bg-white border-stone-200",
    warn: "bg-[color:var(--terracotta-light)] border-[color:var(--terracotta)]/30",
    danger: "bg-rose-50 border-rose-200",
    ok: "bg-emerald-50 border-emerald-200",
  };
  return (
    <div className={`rounded-xl border ${tones[tone]} p-3`}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-stone-500">{label}</div>
      <div className="font-mono-num text-lg font-bold text-stone-900 mt-0.5">{value}</div>
    </div>
  );
}

function MiniStat({ label, value, sub, tone = "default" }) {
  const tones = {
    default: "text-stone-900",
    warn: "text-amber-700",
    danger: "text-rose-700",
    ok: "text-emerald-700",
  };
  return (
    <div className="rounded-lg bg-stone-50 px-2 py-2 text-center">
      <div className={`font-mono-num text-base font-bold ${tones[tone]}`}>{value}</div>
      <div className="text-[9px] font-bold uppercase tracking-wider text-stone-500">{label}</div>
      {sub && <div className="text-[9px] text-stone-400">{sub}</div>}
    </div>
  );
}
